import React, { useCallback } from 'react';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components Styled
import { Container, ContainerButton } from '../components/MainStyledComponents';

// Components
import { MainButton } from '../../../../components/inputs/buttons/MainButton';
import { CampaignTable } from '../../../../components/display/tables/CampaignTable';

// Types
import { OrderType } from '../../../../types/orders';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { dataTypes } from '../../../../components/display/tables/CampaignTable/types';

// Utils
import { modalIds } from '../../../../components/modals/SuperModal/types';
import { openModal } from '../../../../state/actions/ui/modal';

// Hooks
import { usePersistentSelection } from '../../../../hooks/usePersistentSelection';
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';

// GQL
import { client } from '../../../../gql/client';
import { GET_CAMPAIGNS } from '../../../../gql/queries/campaign';
import { selectAllOrders } from '../../../../gql/selectors/orders';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { store } from '../../../../state/configureStore';

const TABLE_ID = tableIds.SEARCH_AND_CREATE_HISTORICAL_CAMPAIGNS;

const noop = () => undefined;

export default function SearchAndCreateHistoricalCampaignPage(): JSX.Element {
  const { t } = useTranslation(['pages', 'campaigns']);
  const dispatch = useDispatch();
  const data = useAsyncValue() as dataTypes;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  useRevalidateOnSync();

  // Selección persistente multi-página (mismo hook que la vista de históricos
  // de órdenes genéricas). Las claves de selección son ids de entidad Campaign.
  const {
    rowSelection,
    onRowSelectionChange,
    selectedCount,
    selectAllMode,
    excludedIds,
    selectedRows,
    handleSelectAll,
    handleClearSelection,
  } = usePersistentSelection({
    dataAll: data.campaigns || [],
    totalCount: data.totalCount || 0,
  });

  const buildCampaignInput = useCallback((cursor: Record<string, any>) => {
    const effectiveTake = firstPageTake(cursor.take, takeBase);
    return {
      take: effectiveTake,
      cursor: '',
      sortField: cursor.sortField,
      orderBy: cursor.orderBy ?? 'desc',
      name: cursor.name,
      fromDate: cursor.fromDate,
      toDate: cursor.toDate,
      concessionId: cursor.concessionId,
      predefinedOrderId: cursor.predefinedOrderId,
      states: cursor.states,
      userIds: cursor.userIds,
      teamIds: cursor.teamIds ?? [],
      track: cursor.track,
      pkInitKm: cursor.pkInitKm,
      pkInitMeter: cursor.pkInitMeter,
      pkEndKm: cursor.pkEndKm,
      pkEndMeter: cursor.pkEndMeter,
      // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente. Lo comparten
      // la búsqueda, su setCursor y el polling.
      onlyFlagged: cursor.onlyFlagged,
    };
  }, []);

  const onReFetchData = useCallback(async () => {
    const currentCursor = getCursor(TABLE_ID)(store.getState());
    const input = buildCampaignInput(currentCursor);
    const { data: result } = await client.query({
      query: GET_CAMPAIGNS,
      variables: { input },
      fetchPolicy: 'network-only',
    });
    // OMIT sortField + orderBy en setCursorData: el merge de cursorTables
    // preserva los del cursor activo (patrón canónico Campaigns/index.tsx —
    // dispatchearlos aquí pisaría el 'asc' recién seteado por SortableHeader).
    const {
      sortField: _sortField,
      orderBy: _orderBy,
      ...setCursorData
    } = input;
    dispatch(setCursor(TABLE_ID, setCursorData));
    await selectAllOrders(result.campaigns.campaigns);
    revalidator.revalidate();
  }, [buildCampaignInput, dispatch, revalidator]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, [revalidator]);

  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(TABLE_ID)(store.getState());
    await client.query({
      query: GET_CAMPAIGNS,
      variables: { input: buildCampaignInput(currentCursor) },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, [buildCampaignInput, revalidator]);
  usePollingRevalidation(onPollingRefresh);

  // Ver la campaña seleccionada (paridad con "Ver orden" de históricos genéricos).
  // El id de fila es el de la Order padre (selectAllOrders → id: order.id), mismo
  // valor que usa el double-tap de la tabla para abrir el detalle de campaña.
  const handleSeeCampaign = useCallback(() => {
    const firstSelected = selectedRows[0];
    if (firstSelected) navigate(`/work-orders/campaign/${firstSelected}`);
  }, [navigate, selectedRows]);

  const handleDownloadHistory = useCallback(() => {
    const currentCursor = getCursor(TABLE_ID)(store.getState());
    const {
      id: _id,
      take: _take,
      cursor: _cursor,
      ...filterFields
    } = (currentCursor || {}) as any;
    dispatch(
      openModal(modalIds.DOWNLOAD_HISTORIC, {
        type: OrderType.Campaign,
        entityType: 'campaign',
        showCombinedReport: true,
        filterPositionColumn: 2,
        selectedRows: selectAllMode ? [] : selectedRows,
        selectAll: selectAllMode,
        excludedIds: selectAllMode ? [...excludedIds] : [],
        filters: filterFields,
        totalCount: data.totalCount || 0,
      })
    );
  }, [dispatch, selectAllMode, selectedRows, excludedIds, data.totalCount]);

  return (
    <Container>
      <CampaignTable
        titleText={t('campaigns.filterBy')}
        data={data}
        setRowState={noop}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={TABLE_ID}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        selectAllMode={selectAllMode}
        excludedIds={excludedIds}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        selectedCount={selectedCount}
        showFlagColumn
        showOnlyFlaggedFilter
      />
      <ContainerButton>
        <MainButton
          variant="secondary"
          text={t('pages:campaigns.view')}
          disabled={selectedCount !== 1}
          onClick={handleSeeCampaign}
        />
        <MainButton
          text={t('pages:workOrders.downloadHistoric')}
          disabled={selectedCount === 0}
          onClick={handleDownloadHistory}
        />
      </ContainerButton>
    </Container>
  );
}
